import { useSyncExternalStore } from "react";
import { Link } from "react-router-dom";
import { Wallet, WalletCards, Clock, ArrowRightLeft } from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { useWalletStore } from "@/stores/wallet-store";
import { useTransferStore } from "@/stores/transfer-store";
import { cn } from "@/lib/utils";
import {
  getTotalAum,
  getWalletChainCounts,
  getActiveWalletCount,
  getTransferStats,
  getPendingApprovals,
  usdCompact,
} from "../dashboard-selectors";

function useIsClient() {
  return useSyncExternalStore(
    () => () => {},
    () => true,
    () => false
  );
}

function greetingForHour(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function todayLabel() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

const KPI_TONE: Record<string, { iconBg: string; iconColor: string }> = {
  aum: { iconBg: "bg-blue-50", iconColor: "text-blue-600" },
  wallets: { iconBg: "bg-emerald-50", iconColor: "text-emerald-600" },
  approvals: { iconBg: "bg-amber-50", iconColor: "text-amber-600" },
  transfers: { iconBg: "bg-indigo-50", iconColor: "text-indigo-600" },
};

function DecorativeHeroArt() {
  return (
    <svg
      viewBox="0 0 360 260"
      className="pointer-events-none absolute -right-4 top-0 h-full w-auto opacity-70"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <defs>
        <linearGradient id="hero-grad-1" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--ads-accent)" stopOpacity="0.22" />
          <stop offset="100%" stopColor="var(--ads-viz-blue-soft)" stopOpacity="0.06" />
        </linearGradient>
        <linearGradient id="hero-grad-2" x1="1" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--ads-viz-violet)" stopOpacity="0.18" />
          <stop offset="100%" stopColor="var(--ads-viz-violet-soft)" stopOpacity="0.05" />
        </linearGradient>
        <linearGradient id="hero-grad-3" x1="0.5" x2="0.5" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--ads-viz-teal)" stopOpacity="0.14" />
          <stop offset="100%" stopColor="var(--ads-viz-teal-soft)" stopOpacity="0.04" />
        </linearGradient>
      </defs>
      <path d="M280 20 C340 60 360 140 320 200 C280 260 200 250 160 210 C120 170 140 100 200 70 C240 50 260 10 280 20Z" fill="url(#hero-grad-1)" />
      <path d="M220 80 C270 100 300 160 270 210 C240 260 170 250 150 200 C130 150 160 100 220 80Z" fill="url(#hero-grad-2)" />
      <path d="M180 150 C230 170 260 220 220 250 C180 280 120 260 110 220 C100 180 130 150 180 150Z" fill="url(#hero-grad-3)" />
    </svg>
  );
}

export function HeroSection() {
  const user = useAuthStore((s) => s.user);
  const isClient = useIsClient();

  const wallets = useWalletStore((s) => s.wallets);
  const transfers = useTransferStore((s) => s.transfers);
  const approvals = useTransferStore((s) => s.approvals);

  const totalAum = getTotalAum(wallets);
  const activeWalletCount = getActiveWalletCount(wallets);
  const { walletCount } = getWalletChainCounts(wallets);
  const { volumeToday, total30dHint } = getTransferStats();
  const pendingApprovals = getPendingApprovals(approvals);
  const todayTransferCount = transfers.length;

  const firstName = user?.name?.split(" ")[0] ?? "Operator";
  const greeting = isClient ? `${greetingForHour(new Date().getHours())},` : "Welcome back,";
  const date = isClient ? todayLabel() : "—";

  const kpis = [
    {
      key: "aum",
      icon: Wallet,
      label: "Total AUM",
      value: usdCompact.format(totalAum),
      sub: "Live wallet-store AUM",
      href: "/wallets",
    },
    {
      key: "wallets",
      icon: WalletCards,
      label: "Active Wallets",
      value: String(activeWalletCount),
      sub: `of ${walletCount} provisioned`,
      href: "/wallets",
    },
    {
      key: "approvals",
      icon: Clock,
      label: "Pending Approvals",
      value: String(pendingApprovals.length),
      sub: pendingApprovals.length > 0 ? "require action" : "all caught up",
      href: "/approvals",
    },
    {
      key: "transfers",
      icon: ArrowRightLeft,
      label: "Transfers Today",
      value: volumeToday,
      sub: `${todayTransferCount} transaction${todayTransferCount === 1 ? "" : "s"} today`,
      href: "/transfers",
    },
  ] as const;

  return (
    <section className="relative overflow-hidden rounded-2xl border border-line bg-gradient-to-br from-blue-50/70 via-white to-indigo-50/40 p-5 md:p-6">
      <DecorativeHeroArt />

      <div className="relative z-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-muted">{greeting}</div>
            <h1 className="text-3xl font-extrabold tracking-tight text-ink md:text-4xl">{firstName}!</h1>
            <p className="mt-1 text-sm text-muted">
              Here&apos;s what&apos;s happening with your digital asset operations today.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="text-xs font-medium text-muted" suppressHydrationWarning>{date}</div>
            <div className="flex items-center rounded-lg border border-line bg-white p-0.5 text-xs font-semibold">
              {["1D", "7D", "30D", "90D"].map((r, idx) => {
                const active = idx === 0;
                if (active) {
                  return (
                    <span
                      key={r}
                      className="rounded-md bg-primary px-2.5 py-1 text-white"
                      aria-current="true"
                    >
                      {r}
                    </span>
                  );
                }
                return (
                  <span
                    key={r}
                    title="Historical data coming soon."
                    aria-label={`${r} — Historical data coming soon.`}
                    className="rounded-md px-2.5 py-1 text-muted/60 transition-colors hover:text-muted"
                  >
                    {r}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          {kpis.map((kpi) => {
            const tone = KPI_TONE[kpi.key];
            const Icon = kpi.icon;
            return (
              <Link
                key={kpi.label}
                to={kpi.href}
                className="group relative flex items-center gap-3 rounded-xl border border-line bg-white p-3.5 transition-all hover:border-primary/40 md:p-4"
              >
                <span className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-xl", tone.iconBg)}>
                  <Icon className={cn("size-5", tone.iconColor)} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-muted">{kpi.label}</div>
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold tracking-tight text-ink md:text-2xl">{kpi.value}</span>
                    {kpi.key === "transfers" && total30dHint && (
                      <span className="inline-flex items-center gap-0.5 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-600">
                        ↑ {total30dHint}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted">{kpi.sub}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
